import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DefaultLayout from '../../layout/DefaultLayout';
import Loading from '../../components/Loading';
import Alert from '../../components/Alert';
import creditoService from '../../services/creditoService';
import userService from '../../services/userService';
import { useGlobalState } from '../../state';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { toCurrency } from '../../utils/formaters';

type ArchivoEntry = { uri: File | string | null; type?: string };

const validationSchema = yup.object().shape({
    telefono: yup
        .string()
        .transform((value: any) => {
            if (typeof value !== 'string') return value;        
            // eliminar todo lo que no sea dígito antes de validar
            return value.replace(/\D/g, '');
        })
        .required('El teléfono es obligatorio')
        .matches(/^[0-9]{6,15}$/, 'El teléfono debe contener solo números (6 a 15 dígitos)'),
    email: yup
        .string()
        .transform((value: any) => (typeof value === 'string' ? value.trim() : value))
        .required('El email es obligatorio')
        .email('El email debe ser válido'),
});

const STORAGE_KEY = 'resultadoSimulacion';

const CargaDocumentos: React.FC = () => {
    const navigate = useNavigate();
    const [user] = useGlobalState('user');

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | undefined>(undefined);
    const [lineaCredito, setLineaCredito] = useState<any | undefined>(undefined);
    const [resultadoSimulacion, setResultadoSimulacion] = useState<any | null>(null);
    const [datosContacto, setDatosContacto] = useState<boolean>(false);
    const [contactData, setContactData] = useState<any[] | undefined>(undefined);

    const [archivos, setArchivos] = useState<Record<string, ArchivoEntry>>({});
    const [erroresArchivos, setErroresArchivos] = useState<Record<string, string>>({});
    const fileInputsRef = useRef<Record<string, HTMLInputElement | null>>({});

    const { control, handleSubmit, formState: { errors, isValid }, setValue, trigger, getValues } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onBlur', // <- cambiar aquí (antes: 'onChange')
        defaultValues: { telefono: '', email: '' },
    });

    useEffect(() => {
        const fetch = async () => {
            setLoading(true);
            setError(undefined);

            if (user === undefined) {
                // still checking auth
                return;
            }
            if (!user) {
                navigate('/auth/signin');
                return;
            }

            try {
                const stored = localStorage.getItem(STORAGE_KEY);
                if (!stored) {
                    setError('No se encontró la simulación previa.');
                    setLoading(false);
                    return;
                }
                const resultado = JSON.parse(stored);
                setResultadoSimulacion(resultado);

                // lineaCreditoJson might be a JSON string
                const lineaDeCreditoJson = resultado.lineaCreditoJson ? JSON.parse(resultado.lineaCreditoJson) : resultado.lineaDeCredito;
                const idLinea = lineaDeCreditoJson?.Id ?? lineaDeCreditoJson?.id ?? resultado.lineaDeCredito?.id;
                if (!idLinea) {
                    setError('No se pudo determinar la línea de crédito');
                    setLoading(false);
                    return;
                }

                const res: any = await creditoService.getLineaCreditoById(idLinea);

                if (res?.success) {
                    setLineaCredito(res.data);
                } else {
                    setError(res?.errors?.[0] ?? 'Error al recuperar la línea de crédito');
                }
            } catch (ex: any) {
                setError(ex?.message ?? 'Ocurrió un error al cargar la petición');
            } finally {
                setLoading(false);
            }
        };

        fetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    useEffect(() => {
        // load contact data if needed when switching to datosContacto
        const fetchContactData = async () => {
            if (!user) return;
            setLoading(true);
            try {
                const res: any = await userService.getDatosUtiles(user?.legajoPrevisional);
                if (res?.success) {
                    setContactData(res.data || []);
                } else {
                    // ignore, user can type manually
                }
            } catch {
                // ignore
            } finally {
                setLoading(false);
            }
        };

        if (datosContacto && !contactData) fetchContactData();
    }, [datosContacto, user]);

    useEffect(() => {
        // pre-fill phone/email from contactData
        if (contactData && contactData.length > 0) {
            const telefonos = contactData.filter((d: any) => d.refTipoDatoUtil === 1 || d.refTipoDatoUtil === 2);
            const emails = contactData.filter((d: any) => d.refTipoDatoUtil === 3);
            if (telefonos.length) setValue('telefono', `${telefonos[0].nCaracteristica || ''}${telefonos[0].cDato || ''}`, { shouldValidate: true });
            if (emails.length) setValue('email', emails[0].cDato || '', { shouldValidate: true });
            // ya validamos los campos concretos al setearlos, no hace falta trigger() global
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactData]);

    const openFilePicker = (idDocumento: number, accept: string) => {
        const key = String(idDocumento);
        const input = fileInputsRef.current[key];
        if (input) {
            // set accept dynamically
            input.accept = accept || '*/*';
            input.click();
        }
    };

    const onFileChange = (idDocumento: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        guardarArchivo(idDocumento, f, f.type);
    };

    const guardarArchivo = (idDocumento: number, uri: string | File, type: string) => {
        setArchivos(prev => ({
            ...prev,
            [String(idDocumento)]: { uri, type }
        }));
        setErroresArchivos(prev => {
            const copy = { ...prev };
            delete copy[String(idDocumento)];
            return copy;
        });
    };

    const handleValidarDocumentos = () => {
        const errores: Record<string, string> = {};
        const documentos = lineaCredito?.documentosLineaCredito ?? [];
        documentos.forEach((item: any) => {
            const id = String(item.documentoRequerido.id);
            if (!archivos[id]) {
                errores[id] = 'Este documento es obligatorio.';
            }
        });
        setErroresArchivos(errores);
        if (Object.keys(errores).length === 0) {
            setDatosContacto(true);
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const solicitarCredito = async (formValues: any) => {
        if (!user || !resultadoSimulacion) {
            setError('No se puede completar la solicitud en este momento.');
            return;
        }
        setLoading(true);
        setError(undefined);

        try {
            const crearCreditoDto: any = {
                idLineaDeCredito: resultadoSimulacion.lineaDeCredito?.Id ?? resultadoSimulacion.lineaDeCredito?.id,
                idAfiliado: user.datosUsuario.beneficiario ? user.datosUsuario.beneficiario.afiliadoId : user.datosUsuario.idAfiliadoOrigen,
                cantidadDeCuotas: resultadoSimulacion.cantidadDeCuotas,
                monto: resultadoSimulacion.monto,
                generarReporte: false,
                telefonoContacto: formValues.telefono,
                emailContacto: formValues.email,
            };

            const formData = new FormData();
            formData.append('IdLineaDeCredito', String(crearCreditoDto.idLineaDeCredito));
            formData.append('IdAfiliado', String(crearCreditoDto.idAfiliado));
            formData.append('CantidadDeCuotas', String(crearCreditoDto.cantidadDeCuotas));
            formData.append('Monto', String(crearCreditoDto.monto));
            formData.append('GenerarReporte', String(crearCreditoDto.generarReporte));
            formData.append('TelefonoContacto', crearCreditoDto.telefonoContacto ?? '');
            formData.append('EmailContacto', crearCreditoDto.emailContacto ?? '');

            // append files
            let index = 0;
            for (const [id, entry] of Object.entries(archivos)) {
                if (!entry || !entry.uri) continue;
                const isFile = typeof entry.uri !== 'string';
                const fileName = isFile ? (entry.uri as File).name : (entry.uri as string).split('/').pop() ?? `archivo_${index}`;
                const mimeType = entry.type ?? (isFile ? (entry.uri as File).type : 'application/octet-stream');

                formData.append(`Files[${index}].IdDocumentoRequerido`, id);
                formData.append(`Files[${index}].FileType`, mimeType);

                const fileToSend = isFile
                    ? (entry.uri as File)
                    : {
                        uri: entry.uri as string,
                        name: fileName,
                        type: mimeType,
                    };

                formData.append(`Files[${index}].File`, fileToSend as any);
                index++;
            }

            const res: any = await creditoService.solicitar(formData);
            if (res?.success) {
                try { localStorage.setItem('idCredito', String(res.data)); } catch { }
                navigate('/prestamos/resultado-crear');
            } else {
                setError(res?.errors?.[0] ?? 'Ha ocurrido un error al solicitar el préstamo.');
            }
        } catch (ex: any) {
            setError(ex?.message ?? 'Ocurrió un error al completar la solicitud');
        } finally {
            setLoading(false);
        }
    };

    return (
        <DefaultLayout>
            <div className="p-6">
                <div className="max-w-4xl mx-auto">
                    <h1 className="text-2xl font-semibold mb-4">Adjuntar documentación</h1>

                    {loading && <Loading statusText="Cargando..." />}

                    {error && <Alert type="failure" message={error} />}

                    {!loading && !datosContacto && lineaCredito && (
                        <>
                            <p className="text-sm text-gray-500 mb-4">Para solicitar tu préstamo deberás cargar la siguiente documentación:</p>

                            <div className="space-y-4">
                                {(lineaCredito.documentosLineaCredito ?? []).length > 0 ? (
                                    (lineaCredito.documentosLineaCredito ?? []).map((item: any, idx: number) => {
                                        const doc = item.documentoRequerido;
                                        const id = String(doc.id);
                                        const accepted = '*/*';
                                        const current = archivos[id];
                                        return (
                                            <div key={id} className="bg-white dark:bg-boxdark p-4 rounded shadow-sm">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <div className="text-sm font-semibold">{idx + 1}. {doc.nombre}</div>
                                                        <div className="text-sm text-gray-600 mt-1">{doc.descripcion}</div>
                                                        <div className="text-sm text-gray-500 mt-1">Formatos: {doc.tiposArchivoJson} • Tamaño máximo: 10MB</div>
                                                    </div>

                                                    <div className="flex flex-col items-end gap-2">
                                                        <button
                                                            type="button"
                                                            onClick={() => openFilePicker(doc.id, accepted)}
                                                            className="px-3 py-2 bg-primario-celeste-800 hover:bg-primario-azul-900 text-white rounded"
                                                        >
                                                            Cargar
                                                        </button>

                                                        {current && current.uri && (
                                                            <div className="text-sm text-green-600">
                                                                {typeof current.uri === 'string' ? (current.uri as string).split('/').pop() : (current.uri as File).name}
                                                            </div>
                                                        )}

                                                        {erroresArchivos[id] && <div className="text-sm text-red-600">{erroresArchivos[id]}</div>}

                                                        {/* hidden input */}
                                                        <input
                                                            ref={el => (fileInputsRef.current[id] = el)}
                                                            type="file"
                                                            accept={accepted || '*/*'}
                                                            style={{ display: 'none' }}
                                                            onChange={(e) => onFileChange(doc.id, e)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div className="bg-white dark:bg-boxdark p-4 rounded text-center text-gray-600">
                                        En este momento no se requiere la carga de ningún documento.
                                    </div>
                                )}
                            </div>

                            <div className="mt-6">
                                <button
                                    onClick={handleValidarDocumentos}
                                    className="inline-flex items-center gap-2 bg-primario-celeste-800 text-white px-4 py-2 rounded"
                                >
                                    Continuar
                                </button>
                            </div>
                        </>
                    )}

                    {!loading && datosContacto && (
                        <>
                            <p className="text-sm text-gray-500 mb-4">Usaremos este número de teléfono y este correo para comunicarnos con vos sobre tu préstamo</p>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Número de teléfono</label>
                                    <Controller
                                        control={control}
                                        name="telefono"
                                        render={({ field: { onChange, onBlur, value } }) => (
                                            <>
                                                <input
                                                    type="text"
                                                    value={value ?? ''}
                                                    onChange={(e) => { onChange(e.target.value); /* no validar aquí */ }}
                                                    onBlur={() => trigger('telefono')} // valida al perder foco
                                                    className={`mt-1 block w-full rounded-md border ${errors.telefono ? 'border-red-500' : 'border-gray-300'} p-2`}
                                                />
                                                {errors.telefono && <p className="text-red-600 text-sm mt-1">{(errors.telefono as any).message}</p>}
                                            </>
                                        )}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Correo electrónico</label>
                                    <Controller
                                        control={control}
                                        name="email"
                                        render={({ field: { onChange, onBlur, value } }) => (
                                            <>
                                                <input
                                                    type="email"
                                                    value={value ?? ''}
                                                    onChange={(e) => { onChange(e.target.value); /* no validar aquí */ }}
                                                    onBlur={() => trigger('email')}
                                                    className={`mt-1 block w-full rounded-md border ${errors.email ? 'border-red-500' : 'border-gray-300'} p-2`}
                                                />
                                                {errors.email && <p className="text-red-600 text-sm mt-1">{(errors.email as any).message}</p>}
                                            </>
                                        )}
                                    />
                                </div>

                                <div className="mt-6">
                                    <button
                                        onClick={handleSubmit(solicitarCredito)}
                                        disabled={!isValid || loading}
                                        className={`inline-flex items-center gap-2 px-4 py-2 rounded font-semibold text-white ${(!isValid || loading) ? 'bg-gray-400' : 'bg-primario-celeste-800'}`}
                                    >
                                        Finalizar
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    {!loading && !lineaCredito && !error && (
                        <div className="bg-white dark:bg-boxdark p-4 rounded text-center text-gray-600">No hay datos disponibles</div>
                    )}
                </div>
            </div>
        </DefaultLayout>
    );
};

export default CargaDocumentos;